/// <amd-module name="Wiki/Editor/PageSaveSource" />
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { VssServerError } from "VSS/Platform/Fetch";
import { combineUrlPaths } from "VSS/Platform/Util/Url";

import { WikiPageMoveResponse, WikiPageResponse, WikiV2 } from "Wiki/Clients/Wiki/Contracts/Wiki";
import { IWikiRestClient } from "Wiki/Clients/Wiki/RestClient/Wiki";
import { getReferredAttachments } from "Wiki/Editor/AttachmentHelpers";
import { IWikiEditorState } from "Wiki/Editor/Contracts";
import { getWikiUpdateError } from "Wiki/Editor/ErrorHelper";
import { isRename } from "Wiki/Editor/Helpers";
import * as Resources from "Wiki/Editor/Resources";
import { WikiInfo, WikiPageInfoWithName } from "Wiki/ViewCommon/Contracts";
import { TelemetryConstants } from "Wiki/ViewCommon/CustomerIntelligenceConstants";
import { getDepthOfPage } from "Wiki/ViewCommon/Utils/PathHelper";
import { getWikiTypeString, publish } from "Wiki/ViewCommon/Utils/WikiHelper";

export class PageSaveSource {
    private _wikiClient: IWikiRestClient;
    private _savedAttachments: string[];

    constructor(private _pageContext: IVssPageContext) {
        this._wikiClient = this._pageContext.getRestClient<IWikiRestClient>("IWikiRestClient");
    }

    public get savedAttachments(): string[] | undefined {
        return this._savedAttachments;
    }

    public savePage = async (
        wikiInfo: WikiInfo,
        pageInfo: WikiPageInfoWithName,
        editorState: IWikiEditorState,
        commitMessage: string
    ): Promise<WikiPageResponse | void> => {
        this._savedAttachments = [];

        try {
            await this._saveAttachments(wikiInfo, editorState);
        } catch (error) {
            const vssServerError = error as VssServerError;
            if (vssServerError.serverError && vssServerError.serverError.typeKey === "InvalidArgumentValueException") {
                throw error;
            } else {
                throw new Error(Resources.Attachment_SaveFailed);
            }
        }

        let pageSaveResponse: void | WikiPageResponse;
        pageSaveResponse = await this._saveContent(wikiInfo, pageInfo, editorState, commitMessage);

        if (isRename(pageInfo, editorState)) {
            const parentPath = pageInfo.parentPath;
            const newPagePath = combineUrlPaths(parentPath, editorState.pageName.trim());
            const initialPagePath = combineUrlPaths(parentPath, pageInfo.pageName);

            let moveResponse: WikiPageMoveResponse;
            moveResponse = await this._renamePage(wikiInfo, newPagePath, initialPagePath, 0, commitMessage);

            return Promise.resolve({
                eTag: moveResponse.eTag,
                page: {
                    ...moveResponse.pageMove.page,
                    content: pageSaveResponse ? pageSaveResponse.page.content : pageInfo.content
                }
            });
        } else {
            return Promise.resolve(pageSaveResponse);
        }
    };

    private async _saveAttachments(wikiInfo: WikiInfo, editorState: IWikiEditorState): Promise<void> {
        const attachments = getReferredAttachments(editorState.pageContent || "", editorState.attachments);

        if (!attachments || attachments.length === 0) {
            return Promise.resolve();
        }

        for (const attachment of attachments) {
            await this._wikiClient.createAttachment(
                attachment.base64Content,
                wikiInfo.wiki.projectId,
                wikiInfo.wiki.id,
                attachment.file.guidSuffixedFileName,
                wikiInfo.wikiVersion
                // version - Task #1308767 - Save attachment to correct version of the wiki
            );
            this._savedAttachments.push(attachment.file.guidSuffixedFileName);
        }

        return;
    }

    private async _saveContent(
        wikiInfo: WikiInfo,
        pageInfo: WikiPageInfoWithName,
        editorState: IWikiEditorState,
        commitMessage: string
    ): Promise<WikiPageResponse | void> {
        const content = editorState.legacyScripts.wikiAutoCompleteNwpWrapperModule.translateDisplayNamesToStorageKeysOfPersonMentions(
            editorState.pageContent || ""
        );
        const initialContent = pageInfo.content;

        if (content === initialContent && pageInfo.pageName) {
            return Promise.resolve();
        }

        if (pageInfo.version === undefined) {
            if ($DEBUG) {
                throw new Error("attempting to save page without etag");
            }
            throw new Error();
        }
        const pagePath = combineUrlPaths(pageInfo.parentPath, pageInfo.pageName || editorState.pageName.trim()); // incase of new page we need pagename from editor state

        try {
            const pageResponse = await this._wikiClient.createOrUpdatePage(
                {
                    content
                },
                wikiInfo.wiki.projectId,
                wikiInfo.wiki.id,
                pagePath,
                pageInfo.version,
                commitMessage,
                wikiInfo.wikiVersion
            );
            return pageResponse;
        } catch (error) {
            const vssServerError = error as VssServerError;
            if (vssServerError.serverError && vssServerError.serverError.typeKey === "WikiPageHasConflictsException") {
                publish(this._pageContext, TelemetryConstants.ConflictErrorOnPageSave, wikiInfo.wiki.type, {
                    depth: getDepthOfPage(pageInfo.parentPath) + 1
                });
            }
            throw getWikiUpdateError(error, pagePath);
        }
    }

    private async _renamePage(
        wikiInfo: WikiInfo,
        newPagePath: string,
        originalPagePath: string,
        newPageOrder: number,
        comment?: string
    ): Promise<WikiPageMoveResponse> {
        comment = comment || format(Resources.RenamePageDefaultComment, originalPagePath, newPagePath);

        try {
            const pageMoveResponse = await this._wikiClient.createPageMove(
                {
                    path: originalPagePath,
                    newPath: newPagePath,
                    newOrder: newPageOrder && newPageOrder < 0 ? 0 : newPageOrder
                },
                wikiInfo.wiki.projectId,
                wikiInfo.wiki.id,
                comment,
                wikiInfo.wikiVersion
            );
            // TODO page view stats call
            return pageMoveResponse;
        } catch (error) {
            throw getWikiUpdateError(error, newPagePath, originalPagePath);
        }
    }
}
